<div class="loading-container" *ngIf="loading">
    <p-progressSpinner styleClass="labtech-spinner" ariaLabel="Loading"></p-progressSpinner>
    <p>{{ isEditMode ? 'Loading user...' : 'Creating user...' }}</p>
</div>

<div class="page-container" *ngIf="!loading">

    <!-- Header -->
    <div class="header-section">
        <div class="title-block">
            <h1>{{ isEditMode ? 'Edit User' : 'Create User' }}</h1>
            <div class="subtitle">
                {{ isEditMode ? 'Update user account information' : 'Create a new user account with MFA requirement' }}
            </div>
        </div>

        <div class="header-actions">
            <button class="btn-secondary" (click)="goBack()" [disabled]="loading">
                <span class="material-icons">arrow_back</span>
                Back
            </button>
            <button class="btn-primary" (click)="createUser()" [disabled]="createUserForm.invalid || loading">
                <span class="material-icons">{{ isEditMode ? 'update' : 'save' }}</span>
                {{ loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update User' : 'Create User')
                }}
            </button>
        </div>
    </div>

    <!-- Success/Error Messages -->
    <div class="alert alert-success" *ngIf="success" [@cardAnimation]>
        <span class="material-icons">check_circle</span>
        {{ success }}
    </div>
    <div class="alert alert-error" *ngIf="error" [@cardAnimation]>
        <span class="material-icons">error</span>
        {{ error }}
    </div>

    <!-- Form Card -->
    <form [formGroup]="createUserForm">
        <div class="card">

            <!-- Basic Information Section -->
            <div class="section">
                <div class="section-title">
                    <span class="material-icons">{{ isEditMode ? 'edit' : 'person_add' }}</span>
                    Account Information
                </div>

                <div class="form-grid">

                    <!-- Username -->
                    <div class="form-field">
                        <label>Username *</label>
                        <input class="text-input" formControlName="username" placeholder="max.mustermann">
                        <div class="error-text"
                            *ngIf="createUserForm.get('username')?.invalid && createUserForm.get('username')?.touched">
                            <span class="material-icons">warning</span>
                            Minimum 3 characters required
                        </div>
                    </div>

                    <!-- Email -->
                    <div class="form-field">
                        <label>Email *</label>
                        <input class="text-input" type="email" formControlName="email" placeholder="max@example.com">
                        <div class="error-text"
                            *ngIf="createUserForm.get('email')?.invalid && createUserForm.get('email')?.touched">
                            <span class="material-icons">warning</span>
                            Valid email address required
                        </div>
                    </div>

                    <!-- First Name -->
                    <div class="form-field">
                        <label>First Name *</label>
                        <input class="text-input" formControlName="firstName" placeholder="Max">
                        <div class="error-text"
                            *ngIf="createUserForm.get('firstName')?.invalid && createUserForm.get('firstName')?.touched">
                            <span class="material-icons">warning</span>
                            First Name required
                        </div>
                    </div>

                    <!-- Last Name -->
                    <div class="form-field">
                        <label>Last Name *</label>
                        <input class="text-input" formControlName="lastName" placeholder="Mustermann">
                        <div class="error-text"
                            *ngIf="createUserForm.get('lastName')?.invalid && createUserForm.get('lastName')?.touched">
                            <span class="material-icons">warning</span>
                            Last Name required
                        </div>
                    </div>

                    <!-- Role -->
                    <div class="form-field">
                        <label>Role *</label>
                        <p-select [filter]="true" [options]="roleOptions" optionLabel="label" optionValue="value"
                            filterBy="label" formControlName="role" class="dropdown" placeholder="Select role"
                            appendTo="body">
                        </p-select>
                        <div class="error-text"
                            *ngIf="createUserForm.get('role')?.invalid && createUserForm.get('role')?.touched">
                            <span class="material-icons">warning</span>
                            Role required
                        </div>
                    </div>

                    <!-- Login Method -->
                    <div class="form-field">
                        <label>Login Method *</label>
                        <p-select [filter]="true" [options]="authMethodOptions" optionLabel="label" optionValue="value"
                            filterBy="label" formControlName="authMethods" class="dropdown"
                            placeholder="Select login method" appendTo="body">
                        </p-select>
                        <div class="error-text"
                            *ngIf="createUserForm.get('authMethods')?.invalid && createUserForm.get('authMethods')?.touched">
                            <span class="material-icons">warning</span>
                            Login method required
                        </div>
                    </div>

                    <div class="form-field" *ngIf="selectedAuthMethod === 'Azure'">
                        <label>User Principal Name *</label>
                        <input class="text-input" formControlName="userPrincipalName" placeholder="user@company.com">
                        <div class="error-text"
                            *ngIf="createUserForm.get('userPrincipalName')?.invalid && createUserForm.get('userPrincipalName')?.touched">
                            <span class="material-icons">warning</span>
                            User principal name required
                        </div>
                    </div>

                    <div class="form-field" *ngIf="selectedAuthMethod === 'Azure' && isEditMode">
                        <label>Microsoft Object ID</label>
                        <input class="text-input" formControlName="objectId" readonly
                            placeholder="Assigned after first Microsoft login">
                        <div class="hint">
                            <span class="material-icons">info</span>
                            Filled from Microsoft after the first successful Microsoft login
                        </div>
                    </div>

                    <!-- Action Buttons (Only in Edit Mode, next to Role) -->
                    <div class="form-field action-buttons-field" *ngIf="isEditMode">
                        <label>Account Actions</label>
                        <div class="action-buttons-inline">
                            <!-- Reset Password (nur für MFA/App User) -->
                            <button class="action-btn-compact btn-reset-password"
                                *ngIf="createUserForm.value.authMethods === 'App'" (click)="openResetPasswordDialog()"
                                type="button" title="Set new password for user">
                                <span class="material-icons">lock_reset</span>
                                Reset Password
                            </button>

                            <!-- Reset MFA (nur für MFA/App User mit aktiviertem MFA) -->
                            <button class="action-btn-compact btn-reset-mfa"
                                *ngIf="createUserForm.value.authMethods === 'App' && createUserForm.value.mfaEnabled"
                                (click)="openResetMfaDialog()" type="button" title="Reset MFA setup">
                                <span class="material-icons">phonelink_lock</span>
                                Reset MFA
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Password Section (Only in CREATE mode) -->
            <div class="section" *ngIf="!isEditMode && selectedAuthMethod === 'App'">
                <div class="section-title">
                    <span class="material-icons">lock</span>
                    Initial Password
                </div>

                <div class="form-grid">
                    <div class="form-field" style="grid-column: 1 / -1;">
                        <label>Password *</label>
                        <div class="password-wrapper">
                            <input class="text-input" type="text" formControlName="password"
                                placeholder="Min. 8 characters">
                            <button class="btn-secondary btn-icon" type="button" (click)="generatePassword()"
                                title="Generate random password">
                                <span class="material-icons">casino</span>
                            </button>
                            <button class="btn-secondary btn-icon" type="button" (click)="copyPassword()"
                                title="Copy password">
                                <span class="material-icons">content_copy</span>
                            </button>
                        </div>
                        <div class="error-text"
                            *ngIf="createUserForm.get('password')?.invalid && createUserForm.get('password')?.touched">
                            <span class="material-icons">warning</span>
                            Minimum 8 characters required
                        </div>
                        <div class="hint">
                            <span class="material-icons">info</span>
                            Securely transmit this password to the user (not via email!)
                        </div>
                    </div>
                </div>
            </div>

            <!-- Info Section -->
            <div class="section">
                <div class="section-title">
                    <span class="material-icons">info</span>
                    {{ isEditMode ? 'Information' : 'After Creation' }}
                </div>

                <div class="info-box">
                    <ul *ngIf="!isEditMode && selectedAuthMethod === 'App'">
                        <li>The user receives the login credentials</li>
                        <li>On first login, MFA setup is mandatory</li>
                        <li>MFA is done via Microsoft Authenticator App</li>
                    </ul>
                    <ul *ngIf="!isEditMode && selectedAuthMethod === 'Azure'">
                        <li>Create the user with the same email address used in Microsoft Entra ID</li>
                        <li>The user signs in with the "Login with Microsoft" button</li>
                        <li>No local password is created for Microsoft login users</li>
                    </ul>
                    <ul *ngIf="isEditMode">
                        <li>Only account information can be updated here</li>
                        <li>To reset password, use the "Reset Password" button</li>
                        <li>To reset MFA, use the "Reset MFA" button</li>
                    </ul>
                </div>
            </div>

        </div>
    </form>

</div>

<!-- Reset Password Dialog -->
<ng-container *ngIf="resetPasswordDialog">
    <p-dialog [modal]="true" [visible]="true" header="Reset Password" (onHide)="closeResetPasswordDialog()" [style]="{ width: '450px' }">
        <div class="dialog-content">
            <p class="dialog-info">
                Set a new password for user <strong>{{ createUserForm.value.username }}</strong>
            </p>

            <div class="dialog-form-field">
                <label>New Password *</label>
                <div class="password-wrapper">
                    <input class="text-input" [type]="showNewPassword ? 'text' : 'password'" [(ngModel)]="newPassword"
                        placeholder="Min. 8 characters">
                    <button class="btn-secondary btn-icon" type="button" (click)="showNewPassword = !showNewPassword"
                        title="Toggle password visibility">
                        <span class="material-icons">{{ showNewPassword ? 'visibility_off' : 'visibility' }}</span>
                    </button>
                    <button class="btn-secondary btn-icon" type="button" (click)="generateNewPassword()"
                        title="Generate random password">
                        <span class="material-icons">casino</span>
                    </button>
                    <button class="btn-secondary btn-icon" type="button" (click)="copyNewPassword()"
                        title="Copy password">
                        <span class="material-icons">content_copy</span>
                    </button>
                </div>
            </div>

            <div class="dialog-form-field">
                <label>Confirm Password *</label>
                <input class="text-input" [type]="showConfirmPassword ? 'text' : 'password'"
                    [(ngModel)]="confirmPassword" placeholder="Confirm new password">
            </div>

            <p *ngIf="passwordError" class="error-message">
                <span class="material-icons">warning</span>
                {{ passwordError }}
            </p>

            <div class="hint" style="margin-top: 16px;">
                <span class="material-icons">info</span>
                Securely transmit this password to the user (not via email!)
            </div>
        </div>
        <ng-template pTemplate="footer">
            <button class="btn-secondary" (click)="closeResetPasswordDialog()">Cancel</button>
            <button class="btn-primary" (click)="confirmResetPassword()" [disabled]="!newPassword || !confirmPassword">
                <span class="material-icons">lock_reset</span>
                Reset Password
            </button>
        </ng-template>
    </p-dialog>
</ng-container>
<!-- Reset MFA Dialog -->
<ng-container *ngIf="resetMfaDialog">
    <p-dialog [modal]="true" [visible]="true" header="Reset MFA" (onHide)="closeResetMfaDialog()" [style]="{ width: '450px' }">
        <div class="dialog-content">
            <p class="dialog-info">
                Reset Multi-Factor Authentication for user <strong>{{ createUserForm.value.username }}</strong>
            </p>
            <div class="info-box">
                <span class="material-icons">info</span>
                <span>The user will need to set up MFA again on their next login.</span>
            </div>
        </div>
        <ng-template pTemplate="footer">
            <button class="btn-secondary" (click)="closeResetMfaDialog()">Cancel</button>
            <button class="btn-primary" (click)="confirmResetMfa()">
                <span class="material-icons">phonelink_lock</span>
                Reset MFA
            </button>
        </ng-template>
    </p-dialog>
</ng-container>
